import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faGripVertical,
  faLock,
  faPencil,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { SectionWidthEditor } from '@/features/inspection/components/report/SectionWidthEditor';
import {
  canEditEngineerToggleable,
  canEditVisibility,
  columnsWithMinimum,
  sectionLabel,
  type DraftSection,
} from '../design-draft';
import type { ReportTemplateColumn } from '@/services/report-template.service';

interface Props {
  section: DraftSection;
  index: number;
  /** False for a PUBLISHED/ARCHIVED version, or without `report-template:update`. */
  canEdit: boolean;
  busy: boolean;
  onChange: (patch: Partial<DraftSection>) => void;
  onResize: (columns: ReportTemplateColumn[]) => void;
}

/**
 * A small lock chip. Used wherever a control is deliberately absent rather than
 * merely disabled, so the MSE can see WHY — §7 forbids a switch that looks
 * editable but is not.
 */
function LockedChip({ children, testId }: { children: React.ReactNode; testId: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
      data-testid={testId}
    >
      <FontAwesomeIcon icon={faLock} className="h-2.5 w-2.5" aria-hidden="true" />
      {children}
    </span>
  );
}

/**
 * A labelled checkbox that renders as a real `<input type="checkbox">`.
 *
 * The visible box is decoration (`aria-hidden`); the input itself is
 * screen-reader- and keyboard-operable, and carries the accessible name. The
 * tick is a glyph, not a colour, so state never depends on colour alone (§17).
 */
function DesignCheckbox({
  checked,
  disabled,
  label,
  testId,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  testId: string;
  onChange: () => void;
}) {
  return (
    <label className={cn('flex items-center gap-2', disabled ? 'opacity-60' : 'cursor-pointer')}>
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-label={label}
        data-testid={testId}
      />
      <span
        aria-hidden="true"
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
          'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-ring',
        )}
      >
        {checked && <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5" />}
      </span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </label>
  );
}

/**
 * One section of the master template, as the MSE designs it (B6.9e §6).
 *
 * Every capability shown here is the API's verdict, never inferred from the
 * section key:
 *   - `canBeEngineerToggleable` decides whether the toggle exists at all;
 *   - `isWidthEditable` decides whether the width editor appears;
 *   - `minWidthFraction` bounds the resize;
 *   - `defaultTitle` is what an empty title renders as.
 *
 * The single exception is VISIBILITY on the four structural sections, which is
 * locked by D-079 — a business rule the read API does not expose as a field.
 * `structuralMismatch()` cross-checks that rule against the API's own metadata,
 * so the two can never silently disagree.
 */
export function TemplateSectionRow({ section, index, canEdit, busy, onChange, onResize }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.sectionKey,
    disabled: !canEdit,
  });

  const label = sectionLabel(section);
  const structural = !canEditVisibility(section);
  const toggleable = canEditEngineerToggleable(section);
  const [editingTitle, setEditingTitle] = useState(false);

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className={cn(
        'rounded-lg border bg-card px-3 py-2.5',
        structural ? 'border-border/80 bg-muted/20' : 'border-border',
        !section.isVisible && 'opacity-75',
      )}
      data-testid={`design-section-${section.sectionKey}`}
    >
      <div className="flex items-start gap-2">
        <span className="mt-1 w-5 shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
          {index + 1}.
        </span>

        <div className="min-w-0 flex-1 space-y-2">
          {/* ── Title row ─────────────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-2">
            {editingTitle && canEdit ? (
              <input
                type="text"
                autoFocus
                value={section.title ?? ''}
                placeholder={section.defaultTitle ?? section.sectionKey}
                maxLength={255}
                aria-label={`Section title for ${section.sectionKey}`}
                data-testid={`title-input-${section.sectionKey}`}
                className="h-8 min-w-0 flex-1 rounded-lg border border-input bg-background px-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                onChange={(e) => {
                  // Empty input means "no override" — the API treats null and
                  // absent identically, and it is what `defaultTitle` restores.
                  // Trimming to null here also avoids sending a whitespace-only
                  // title, which the server rejects outright rather than coercing.
                  const next = e.target.value;
                  onChange({ title: next.trim().length === 0 ? null : next });
                }}
                onBlur={() => setEditingTitle(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    e.preventDefault();
                    setEditingTitle(false);
                  }
                  // Arrow keys belong to the text cursor here, not to @dnd-kit's
                  // keyboard sensor — without this, editing a title inside a
                  // lifted row would reorder the template instead.
                  e.stopPropagation();
                }}
              />
            ) : (
              <span
                className="min-w-0 flex-1 truncate text-sm font-medium text-foreground"
                data-testid={`section-label-${section.sectionKey}`}
              >
                {label}
              </span>
            )}

            {canEdit && !editingTitle && (
              <button
                type="button"
                onClick={() => setEditingTitle(true)}
                className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                aria-label={`Edit title of ${label}`}
                data-testid={`edit-title-${section.sectionKey}`}
              >
                <FontAwesomeIcon icon={faPencil} className="h-3 w-3" />
              </button>
            )}

            {canEdit && section.title !== null && (
              <button
                type="button"
                onClick={() => onChange({ title: null })}
                className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                aria-label={`Restore the built-in title for ${label}`}
                data-testid={`reset-title-${section.sectionKey}`}
              >
                <FontAwesomeIcon icon={faRotateLeft} className="h-3 w-3" />
              </button>
            )}

            {canEdit && (
              <button
                type="button"
                className="shrink-0 cursor-grab touch-none rounded px-1 py-1 text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                aria-label={`Reorder ${label}`}
                data-testid={`drag-section-${section.sectionKey}`}
                {...attributes}
                {...listeners}
              >
                <FontAwesomeIcon icon={faGripVertical} className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* The stable identifier, always shown: the MSE renames titles freely,
              so the key is the only thing that reliably identifies a section. */}
          <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            {section.sectionKey}
          </p>

          {/* ── Controls ──────────────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {/* Visibility. Structural sections show a LOCKED chip rather than a
                disabled switch: D-079 makes them permanently visible, and a
                greyed-out control invites the MSE to wonder what would unlock it. */}
            {structural ? (
              <LockedChip testId={`visibility-locked-${section.sectionKey}`}>
                Always visible
              </LockedChip>
            ) : (
              <DesignCheckbox
                checked={section.isVisible}
                disabled={!canEdit || busy}
                label="Visible"
                testId={`visible-${section.sectionKey}`}
                onChange={() => onChange({ isVisible: !section.isVisible })}
              />
            )}

            <DesignCheckbox
              checked={section.hideWhenEmpty}
              disabled={!canEdit || busy}
              label="Hide when empty"
              testId={`hide-when-empty-${section.sectionKey}`}
              onChange={() => onChange({ hideWhenEmpty: !section.hideWhenEmpty })}
            />

            {/* Engineer-toggleability. `canBeEngineerToggleable` is the API's
                answer and the only thing consulted — the key is never inspected. */}
            {toggleable ? (
              <DesignCheckbox
                checked={section.isEngineerToggleable}
                disabled={!canEdit || busy}
                label="Engineer can toggle"
                testId={`engineer-toggleable-${section.sectionKey}`}
                onChange={() => onChange({ isEngineerToggleable: !section.isEngineerToggleable })}
              />
            ) : (
              <LockedChip testId={`engineer-toggleable-locked-${section.sectionKey}`}>
                Engineer cannot toggle
              </LockedChip>
            )}

            {!structural && !section.isVisible && (
              <span
                className="rounded border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                data-testid={`section-hidden-${section.sectionKey}`}
              >
                Hidden
              </span>
            )}
          </div>

          {/* ── Column widths ─────────────────────────────────────────────── */}
          {section.isWidthEditable && (
            <div data-testid={`width-block-${section.sectionKey}`}>
              <SectionWidthEditor
                sectionKey={section.sectionKey}
                sectionLabel={label}
                // The API reports one minimum per SECTION; the shared resizer
                // works per column. Projecting it here is the whole adaptation —
                // the value itself is the server's and is never recomputed.
                columns={columnsWithMinimum(section)}
                canEdit={canEdit && !busy}
                onChange={(columns) =>
                  // Strip the projected minimum back off, so the draft holds
                  // exactly the API's column shape and nothing invented.
                  onResize(
                    columns.map(
                      ({ minWidthFraction: _min, ...rest }) => rest as ReportTemplateColumn,
                    ),
                  )
                }
              />
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
